import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Mail } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/email-subscribe`, { email });
      toast.success('Successfully subscribed to our newsletter!');
      setEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info('Email already subscribed');
      } else {
        toast.error('Subscription failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-gray-900 text-white py-16" data-testid="footer">
      <div className="container mx-auto px-6">
        {/* Newsletter Section */}
        <div className="max-w-2xl mx-auto mb-12" data-testid="newsletter-section">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold mb-2">Stay Connected</h3>
            <p className="text-gray-400">
              Join 5000+ parents and educators who get weekly insights on early childhood development and new courses.
            </p>
          </div>
          <form onSubmit={handleSubscribe} className="flex gap-4" data-testid="subscribe-form">
            <Input
              data-testid="email-subscribe-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 rounded-full px-6 py-6 text-lg"
            />
            <Button 
              data-testid="subscribe-btn"
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-teal-600 to-purple-600 hover:from-teal-700 hover:to-purple-700 text-white rounded-full px-8 py-6 text-lg"
            >
              {loading ? 'Subscribing...' : 'Subscribe'}
              <Mail className="ml-2" />
            </Button>
          </form>
        </div>

        {/* Footer Links */}
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <h4 className="text-xl font-bold mb-4 bg-gradient-to-r from-teal-500 via-blue-500 to-purple-500 bg-clip-text text-transparent">Tinkred</h4>
            <p className="text-gray-400">
              Empowering educators and parents to nurture young minds through evidence-based early childhood education.
            </p>
          </div>
          <div>
            <h4 className="font-bold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-gray-400">
              <li><a href="/" className="hover:bg-gradient-to-r hover:from-teal-500 hover:via-blue-500 hover:to-purple-500 hover:bg-clip-text hover:text-transparent transition-colors">Home</a></li>
              <li><a href="/founder" className="hover:bg-gradient-to-r hover:from-teal-500 hover:via-blue-500 hover:to-purple-500 hover:bg-clip-text hover:text-transparent transition-colors">About Us</a></li>
              <li><a href="/courses" className="hover:bg-gradient-to-r hover:from-teal-500 hover:via-blue-500 hover:to-purple-500 hover:bg-clip-text hover:text-transparent transition-colors">Courses</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold mb-4">Resources</h4>
            <ul className="space-y-2 text-gray-400">
              <li><a href="/courses" className="hover:bg-gradient-to-r hover:from-teal-500 hover:via-blue-500 hover:to-purple-500 hover:bg-clip-text hover:text-transparent transition-colors">Courses</a></li>
              <li><a href="/lesson-plans" className="hover:bg-gradient-to-r hover:from-teal-500 hover:via-blue-500 hover:to-purple-500 hover:bg-clip-text hover:text-transparent transition-colors">Lesson Plans</a></li>
              <li><a href="/genz-programs" className="hover:bg-gradient-to-r hover:from-teal-500 hover:via-blue-500 hover:to-purple-500 hover:bg-clip-text hover:text-transparent transition-colors">Gen Z Programs</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold mb-4">Contact</h4>
            <ul className="space-y-2 text-gray-400">
              <li>Email: email@tinkred.net</li>
              <li>India: +91 9560205515</li>
              <li>UK: +44 7917 672515</li>
              <li>Reach: India & Global</li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
          <p>© 2025 Tinkred. All rights reserved. Keeping the child at heart while being innovative, futuristic, and global.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;